'use client';

import {StepPayload} from '@blesk-frontend/api';
import {forwardRef, ReactNode} from 'react';
import {cn} from '../../libs';

import {ImageRender, type StepViewportUpdate} from './ImageRender';
import {StaticImage} from './StaticImage';

interface StepRenderProps {
  index: number;
  step: StepPayload;
  caption: ReactNode;
  toolbar?: {
    type: 'default' | 'recorder';
    left?: ReactNode;
    right?: ReactNode;
  };
  print?: boolean;
  pdf?: boolean;
  zoom?: boolean;
  onUpdate?: (update: StepViewportUpdate) => void;
}

export const StepRenderer = forwardRef<HTMLDivElement, StepRenderProps>(function StepRenderer(
  {index, step, caption, toolbar, onUpdate, print, pdf = false, zoom = true},
  ref,
) {
  const hasImage = step.src.edited || step.src.original;
  const mode = toolbar ? 'edit' : 'view';

  return (
    <div
      ref={ref}
      className={cn(
        'group relative my-2 break-inside-avoid-page rounded-lg border bg-bg-secondary px-3 py-2 transition-all',
        'md:my-6',
        step.meta.hidden && [
          'border-border-primary/50 bg-bg-secondary/50',
          'hover:bg-bg-secondary',
          'hover:border-border-primary',
        ],
      )}
    >
      {toolbar && (
        <div
          className={cn(
            'pointer-events-none absolute z-30 scale-99',
            'flex items-start justify-between gap-2',
            toolbar.type === 'default' && '-top-4 -right-16 -bottom-4 -left-27 -mt-px p-4',
            toolbar.type === 'recorder' && 'top-1.5 right-1.5',
          )}
        >
          {toolbar.left ?? <div />}
          {toolbar.right ?? <div />}
        </div>
      )}

      <div className="relative z-20 flex flex-col gap-2">
        {hasImage && (
          <div
            key={`step-image-${step.id}`}
            className={cn(
              'select-none overflow-hidden',
              step.meta.hidden && 'opacity-50 transition-opacity hover:opacity-100!',
            )}
          >
            {print ? (
              <StaticImage
                pdf={pdf}
                src={step.src.cropped || step.src.edited || step.src.original}
                alt={step.element.text || ''}
              />
            ) : (
              <ImageRender
                initialScale={1}
                step={step}
                zoomButtons={zoom}
                scrollable={zoom}
                onUpdate={mode === 'edit' ? onUpdate : undefined}
              />
            )}
          </div>
        )}

        <div
          className={cn(
            'flex items-start gap-3 px-2 transition-all md:gap-4 md:px-4',
            step.meta.hidden && 'opacity-35 hover:opacity-100',
          )}
        >
          <div
            className={cn(
              'tiptap-inline-prose prose prose-sm md:prose-base min-w-4 text-nowrap break-words leading-5',
              mode === 'edit' && 'border border-[transparent]',
            )}
          >
            {step.meta.hidden ? '–' : `${index}.`}
          </div>
          <div className="w-full min-w-0 text-pretty">{caption}</div>
        </div>
      </div>
    </div>
  );
});
